import axios from 'axios';
import EventBus from '@/event-bus';
import EditDialog from '@/components/EditDialog.vue';
import YesNoDialog from '@/components/YesNoDialog.vue';
import ObjectRefsDialog from '@/components/ObjectRefsDialog.vue';

import { getErrorMessage } from '@/lib/error';
import { USER_PERMISSIONS } from '@/lib/constants';
import PermissionsCheck from '@/components/PermissionsCheck';
import ProjectMixin from '@/components/ProjectMixin';

export default {
  components: {
    YesNoDialog,
    EditDialog,
    ObjectRefsDialog,
  },

  mixins: [
    PermissionsCheck,
    ProjectMixin,
  ],

  props: {
    projectType: String,
    userId: Number,
    userRole: String,
    user: Object,
  },

  computed: {
    IDFieldName() {
      return 'id';
    },
  },

  data() {
    const allowActions = this.allowActions();

    const headers = this.getHeaders().filter((header) => allowActions || header.value !== 'actions');

    return {
      headers,
      items: null,

      itemId: null,
      editDialog: null,
      deleteItemDialog: null,

      itemRefs: null,
      itemRefsDialog: null,

      // Selection / search / pagination state. Filtering and paging are
      // client-side (Vuetify's :search prop + built-in footer). When a
      // deployment outgrows that, a follow-up can flip selected list
      // endpoints to server-side without touching the views — only
      // loadItems() and the v-data-table props.
      selectedItems: [],
      searchText: '',
      bulkDeleteDialog: null,

      pollIntervalMs: 5000,

      USER_PERMISSIONS,
    };
  },

  async created() {
    await this.beforeLoadItems();
    await this.loadItems();
    // Smart polling: while any visible row is "in flight" (running/pending),
    // re-fetch every pollIntervalMs so a status change shows up even if the
    // WebSocket dropped or this view forgot to subscribe. Views opt in by
    // overriding isItemPending(item).
    this.$_pollTimer = setInterval(() => {
      if (!this.items || !this.items.some((it) => this.isItemPending(it))) return;
      this.loadItems().catch(() => {});
    }, this.pollIntervalMs);
  },

  beforeDestroy() {
    if (this.$_pollTimer) {
      clearInterval(this.$_pollTimer);
      this.$_pollTimer = null;
    }
  },

  methods: {
    allowActions() {
      return this.can(USER_PERMISSIONS.manageProjectResources);
    },

    // eslint-disable-next-line no-empty-function
    async beforeLoadItems() {
    },

    getSingleItemUrl() {
      throw new Error('Not implemented');
    },

    getHeaders() {
      throw new Error('Not implemented');
    },

    getEventName() {
      throw new Error('Not implemented');
    },

    showDrawer() {
      EventBus.$emit('i-show-drawer');
    },

    async onItemSave() {
      await this.loadItems();
    },

    async askDeleteItem(itemId) {
      this.itemId = itemId;

      try {
        this.itemRefs = (await axios({
          method: 'get',
          url: `${this.getSingleItemUrl()}/refs`,
          responseType: 'json',
        })).data;

        if (this.itemRefs.templates.length > 0
          || this.itemRefs.repositories.length > 0
          || this.itemRefs.inventories.length > 0
          || this.itemRefs.access_keys.length > 0
          || this.itemRefs.schedules.length > 0) {
          this.itemRefsDialog = true;
          return;
        }
      } catch (e) {
        // Do nothing
      }

      this.deleteItemDialog = true;
    },

    async deleteItem(itemId) {
      this.itemId = itemId;

      try {
        const item = this.items.find((x) => x[this.IDFieldName] === itemId);

        await axios({
          method: 'delete',
          url: this.getSingleItemUrl(),
          responseType: 'json',
        });

        EventBus.$emit(this.getEventName(), {
          action: 'delete',
          item,
        });

        await this.loadItems();
      } catch (err) {
        EventBus.$emit('i-snackbar', {
          color: 'error',
          text: getErrorMessage(err),
        });
      }
    },

    editItem(itemId) {
      this.itemId = itemId;
      this.editDialog = true;
    },

    async loadItems() {
      this.items = (await axios({
        method: 'get',
        url: this.getItemsUrl(),
        responseType: 'json',
      })).data;
    },

    // Override in views that have in-flight items (running builds, queued
    // tasks, etc.) so smart polling kicks in. Default: nothing pending.
    isItemPending() {
      return false;
    },

    onSearchInput(value) {
      // Vuetify's :search prop reads searchText directly; no debounce
      // needed for client-side filtering of typical list sizes.
      this.searchText = (value || '').trim();
    },

    // Default v-data-table :custom-filter — matches against the item's name
    // only. Views with a different display field (Team.username, Tokens
    // description, etc.) can override `searchableFields` or this method.
    filterByName(value, search, item) {
      if (!search) return true;
      const q = search.toLowerCase();
      const fields = this.searchableFields
        ? this.searchableFields(item)
        : [item && item.name];
      return fields.some((f) => (f || '').toString().toLowerCase().includes(q));
    },

    askBulkDelete() {
      if (!this.selectedItems.length) return;
      this.bulkDeleteDialog = true;
    },

    async bulkDelete() {
      const items = [...this.selectedItems];
      const failed = [];
      // Sequential to avoid the shared this.itemId race that
      // getSingleItemUrl() relies on across most views.
      // eslint-disable-next-line no-restricted-syntax
      for (const item of items) {
        this.itemId = item[this.IDFieldName];
        try {
          // eslint-disable-next-line no-await-in-loop
          await axios({
            method: 'delete',
            url: this.getSingleItemUrl(),
            responseType: 'json',
          });
          EventBus.$emit(this.getEventName(), { action: 'delete', item });
        } catch (e) {
          failed.push({ item, error: getErrorMessage(e) });
        }
      }
      this.selectedItems = [];
      this.bulkDeleteDialog = false;
      if (failed.length) {
        EventBus.$emit('i-snackbar', {
          color: 'error',
          text: `Failed to delete ${failed.length} of ${items.length} items.`,
        });
      }
      await this.loadItems();
    },
  },
};
